import { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import { QuestionSessionBodyContainerProps } from '../../QuestionSessions/QuestionSessionBody/config';
import { useReferentialQuestionContent } from '../../../hooks/content/useReferentialQuestionContent';
import { useStores } from '../../../hooks/useStores';

export interface MoodChipOption {
  key: string;
  label: string;
  selected: boolean;
}

export interface MoodDetailsData extends Record<string, unknown> {
  feelingsTitle: string;
  keywordsTitle: string;
  feelingOptions: MoodChipOption[];
  onToggleFeeling: (key: string) => void;
  keywordOptions: MoodChipOption[];
  onToggleKeyword: (key: string) => void;
}

const FEELING_FALLBACKS: Record<string, string> = {
  FEELING_JOYFUL: 'Joyful', FEELING_CONFIDENT: 'Confident', FEELING_GRATEFUL: 'Grateful',
  FEELING_SURPRISED: 'Surprised', FEELING_AMUSED: 'Amused', FEELING_EXCITED: 'Excited',
  FEELING_CALM: 'Calm', FEELING_TIRED: 'Tired', FEELING_STRESSED: 'Stressed',
  FEELING_SAD: 'Sad', FEELING_ANGRY: 'Angry', FEELING_ANXIOUS: 'Anxious',
};

// which six feelings the design shows depends on the assessment rating
const FEELINGS_BY_MOOD: Record<'good' | 'okay' | 'bad', string[]> = {
  good: ['FEELING_JOYFUL', 'FEELING_CONFIDENT', 'FEELING_GRATEFUL', 'FEELING_SURPRISED', 'FEELING_AMUSED', 'FEELING_EXCITED'],
  okay: ['FEELING_CALM', 'FEELING_TIRED', 'FEELING_CONFIDENT', 'FEELING_SURPRISED', 'FEELING_STRESSED', 'FEELING_AMUSED'],
  bad: ['FEELING_SAD', 'FEELING_ANGRY', 'FEELING_ANXIOUS', 'FEELING_STRESSED', 'FEELING_TIRED', 'FEELING_CALM'],
};

const KEYWORD_FALLBACKS: Record<string, string> = {
  KEYWORD_FRIENDS: 'Friends 🧑‍🤝‍🧑', KEYWORD_COOKING: 'Cooking 🍳', KEYWORD_CHORES: 'Chores 🧹',
  KEYWORD_STUDIES: 'Studies 🎓', KEYWORD_FAMILY: 'Family 👨‍👩‍👧', KEYWORD_GAMES: 'Games 🎮',
  KEYWORD_READING: 'Reading 📖', KEYWORD_OUTINGS: 'Leisure and outings 🎡', KEYWORD_MUSIC: 'Music 🎶',
  KEYWORD_SHOPPING: 'Shopping 🛍️', KEYWORD_SPORTS: 'Sports 🏃', KEYWORD_WORK: 'Work 💼',
  KEYWORD_RELAXATION: 'Relaxation 🧘', KEYWORD_NATURE: 'Nature 🌿', KEYWORD_TRAVEL: 'Travel ✈️',
};

/**
 * Owns the mood chip selections and composes the readable diary entry into
 * the pending response.
 */
const _ContainMoodDetails: FC<QuestionSessionBodyContainerProps> = ({
  question,
  children,
}) => {
  const { treeNav, language } = useStores();
  const rootStore = useStores();
  const { question: questionStore } = useStores();
  const loading = !questionStore.isReady(question._id);
  const { description, question: questionText } = useReferentialQuestionContent(question);
  const diaryLabels = language?.translate?.MOBILE?.DIARY;

  // the assessment slider answered just before decides which feelings to offer
  const sessionId = treeNav.content.session?._id;
  const assessmentValue = (rootStore as any).responses?.responses?.find(
    (response: any) => response.questionKey === 'assessment'
      && (!sessionId || response.sessionId === sessionId),
  )?.value;
  const mood: 'good' | 'okay' | 'bad' = typeof assessmentValue === 'number'
    ? (assessmentValue >= 3 ? 'good' : assessmentValue <= 1 ? 'bad' : 'okay')
    : 'okay';

  const [feelings, setFeelings] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);

  const label = (key: string, fallbacks: Record<string, string>) =>
    diaryLabels?.[key] || fallbacks[key];

  const toggle = (list: string[], setList: (next: string[]) => void) => (key: string) => {
    setList(list.includes(key) ? list.filter(k => k !== key) : [...list, key]);
  };

  useEffect(() => {
    if (!question?._id) {
      return;
    }
    const parts: string[] = [];
    if (feelings.length > 0) {
      parts.push(feelings.map(key => label(key, FEELING_FALLBACKS)).join(', '));
    }
    if (keywords.length > 0) {
      parts.push(keywords.map(key => label(key, KEYWORD_FALLBACKS)).join(', '));
    }
    treeNav.choices.updatePending({
      questionId: question._id,
      questionKey: question.key,
      // stamp the current context: a stale pending from an unfinished
      // session would otherwise leak its ids into this response
      surveyId: treeNav.content.survey?._id,
      pathId: treeNav.content.path?._id,
      sessionId: treeNav.content.session?._id,
      freeText: parts.join('\n'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?._id, feelings, keywords]);

  const data: MoodDetailsData = {
    feelingsTitle: diaryLabels?.FEELING_TITLE || 'What best describes this feeling?',
    keywordsTitle: diaryLabels?.KEYWORDS_TITLE || 'Your day in a few keywords:',
    feelingOptions: FEELINGS_BY_MOOD[mood].map(key => ({
      key,
      label: label(key, FEELING_FALLBACKS),
      selected: feelings.includes(key),
    })),
    onToggleFeeling: toggle(feelings, setFeelings),
    keywordOptions: Object.keys(KEYWORD_FALLBACKS).map(key => ({
      key,
      label: label(key, KEYWORD_FALLBACKS),
      selected: keywords.includes(key),
    })),
    onToggleKeyword: toggle(keywords, setKeywords),
  };

  return children({
    questionId: question._id,
    questionKey: question.key,
    questionType: question.type,
    questionText,
    questionDescription: description,
    loading,
    data,
  });
};

export const ContainMoodDetails = observer(_ContainMoodDetails);
